import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";

export default function PhoneVerify() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    apiGet<any>("/xapi/profile/overview")
      .then((res) => {
        if (!alive) return;
        const prefill =
          res?.user_data?.phone ??
          res?.business_profile?.phone ??
          res?.business_profile?.company_phone ??
          "";
        setPhone(prefill || "");
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  async function sendCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);

    try {
      const res = await apiPost<any>("/xapi/phone/send", { phone, country: "SE" });
      setSent(true);
      setPhone(res.phone || phone);
      setMessage(res.test ? "Dev verification code sent. Use 000000." : "Verification code sent.");
    } catch (err: any) {
      setError(err?.message || "Could not send verification code.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);

    try {
      const res = await apiPost<any>("/xapi/phone/verify", { phone, code, country: "SE" });
      setPhone(res.phone || phone);
      setMessage("Phone verified.");
      setSent(false);
      setCode("");
    } catch (err: any) {
      setError(err?.message || "Invalid or expired verification code.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="section-box skeleton">Loading phone verification...</div>;
  }

  return (
    <div className="max-w-xl card-box space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Verify your phone</h1>
        <p className="text-sm text-gray-500">
          We use phone verification for account security and sensitive actions.
        </p>
      </header>

      {message && <div className="alert-success">{message}</div>}
      {error && <div className="alert-danger">{error}</div>}

      <form onSubmit={sendCode} className="space-y-3">
        <label className="label">Phone number</label>
        <input
          className="input w-full"
          type="tel"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          placeholder="+46701234567"
          autoComplete="tel"
          required
        />
        <p className="text-xs text-gray-500">Use international format, for example +46701234567.</p>
        <button className="btn btn-white" disabled={busy}>
          {busy ? "Sending..." : sent ? "Send code again" : "Send code"}
        </button>
      </form>

      <form onSubmit={verifyCode} className="space-y-3 border-t border-gray-100 pt-5">
        <label className="label">Verification code</label>
        <input
          className="input w-full"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          placeholder="123456"
          required
        />
        <button className="btn" disabled={busy || !sent}>
          {busy ? "Checking..." : "Verify phone"}
        </button>
      </form>

      <Link to="/profile/security" className="text-sm text-gray-500 underline">
        Back to security
      </Link>
    </div>
  );
}
